"use client";

import Link from "next/link";
import { ScrollReveal } from "@/app/ScrollReveal";
import { SiteFooter } from "@/app/SiteFooter";
import { SiteHeader } from "@/app/SiteHeader";
import type { LocaleContext } from "@/lib/i18n";
import { localizeHref } from "@/lib/i18n";
import { getFaqsByCategory } from "@/lib/faqs-i18n";
import { getMessages } from "@/lib/messages";

export function FaqsContent({ locale, country }: LocaleContext) {
  const m = getMessages(locale);
  const ctx = { locale, country };
  const faqs = getFaqsByCategory(locale);

  return (
    <div className="min-h-screen">
      <SiteHeader locale={locale} country={country} subtitle={m.faqs.subtitle} />

      <main id="main-content" className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-24 pt-8">
        <ScrollReveal>
          <div className="eyebrow text-cyan-700/80 dark:text-cyan-300/80">{m.faqs.eyebrow}</div>
          <h1 className="font-display mt-3 text-4xl italic tracking-tight text-[#0f172b] md:text-5xl dark:text-white mask-rise">
            {m.faqs.title}
          </h1>
          <p className="mt-4 max-w-2xl rounded-2xl bg-white/45 px-4 py-3 text-base text-slate-600 ring-1 ring-white/35 backdrop-blur-xl dark:bg-white/10 dark:text-white/70 dark:ring-white/15">
            {m.faqs.introBefore}{" "}
            <Link
              href={localizeHref("/contact", ctx)}
              className="underline underline-offset-4 hover:text-slate-900 dark:hover:text-white"
            >
              {m.faqs.contactLink}
            </Link>
            {m.faqs.introAfter}
          </p>
        </ScrollReveal>

        <div className="hairline mt-16" />

        <section className="mt-12 space-y-6">
          {faqs.map((group, i) => (
            <ScrollReveal key={group.category} delay={i * 80}>
              <div className="service-card premium-border relative overflow-hidden rounded-[2rem] bg-white/40 p-6 ring-1 ring-white/30 backdrop-blur-2xl dark:bg-white/10 dark:ring-white/20">
                <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-transparent dark:from-white/10 dark:via-transparent" />
                <div className="relative">
                  <div className="flex items-baseline justify-between">
                    <h2 className="font-display text-2xl tracking-tight text-slate-900 dark:text-white">
                      {group.category}
                    </h2>
                    <div className="font-stat text-2xl font-medium text-slate-300/80 dark:text-white/20">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="mt-5 space-y-3">
                    {group.items.map((item) => (
                      <details
                        key={item.q}
                        className="group rounded-2xl bg-white/50 p-4 ring-1 ring-white/40 backdrop-blur-xl transition-colors duration-200 open:bg-white/70 dark:bg-black/30 dark:ring-white/15 dark:open:bg-black/40"
                      >
                        <summary className="cursor-pointer list-none text-sm font-semibold text-slate-900 dark:text-white">
                          <span className="inline-flex items-center gap-2.5">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500/70 dark:bg-cyan-300/80" />
                            {item.q}
                          </span>
                          <span className="float-right text-slate-500 transition-transform group-open:rotate-45 dark:text-white/60">
                            +
                          </span>
                        </summary>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-white/70">
                          {item.a}
                        </p>
                      </details>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </section>

        <SiteFooter />
      </main>
    </div>
  );
}
